import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link } from 'react-router-dom';
import { Box, AppBar, Toolbar, Typography, Drawer, List, ListItem, ListItemIcon, ListItemText, CssBaseline, Container, IconButton, Button, Paper, CircularProgress, Chip, LinearProgress, Badge, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { styled } from '@mui/material/styles';
import MenuIcon from '@mui/icons-material/Menu';
import DashboardIcon from '@mui/icons-material/Dashboard';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AssessmentIcon from '@mui/icons-material/Assessment';
import PeopleIcon from '@mui/icons-material/People';
import FolderIcon from '@mui/icons-material/Folder';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import LogoutIcon from '@mui/icons-material/Logout';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import NotificationsIcon from '@mui/icons-material/Notifications';
import FilterListIcon from '@mui/icons-material/FilterList';

import './App.css';
import { useAppContext } from './context/AppContext';
import { useAuth0 } from '@auth0/auth0-react';
import { useAuth } from './context/Auth0Context';
import Auth0Login from './components/Auth/Auth0Login';
import ProtectedRoute from './components/Auth/ProtectedRoute';
import { useNotifications } from './context/NotificationContext';

import { NotificationProvider } from './context/NotificationContext';
import axios from 'axios';

// Import components
import WeeklyPlan from './components/PlanningModule/WeeklyPlan';
// GanttChart is imported but not used directly in this file
// import { GanttChart } from './components/PlanningModule/GanttChart';
import DailyEntry from './components/TimeLogging/DailyEntry';
import SummaryCards from './components/Dashboard/SummaryCards';
import VarianceCharts from './components/Dashboard/VarianceCharts';
import UserList from './components/UserManagement/UserList';
import ProjectList from './components/ProjectManagement/ProjectList';
import ReportsPage from './components/Reports/ReportsPage';
import PlanningVsActual from './components/Reconciliation/PlanningVsActual';
import AdminPage from './components/Admin/AdminPage';
import AIChat from './components/AIChat';
import NotificationCenter from './components/Notifications/NotificationCenter';
import NotificationsPage from './components/Notifications/NotificationsPage';

// Dashboard Page
const Dashboard = () => {
  // Use the shared context instead of local state
  const { dashboardData, loading, error, projects } = useAppContext();
  const { isAdmin } = useAuth();

  // Filter state for administrators
  const [filters, setFilters] = useState({
    projectName: '',
    projectStage: '',
    department: ''
  });

  // Data for filter dropdowns
  const [projectStages, setProjectStages] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [filteredDashboardData, setFilteredDashboardData] = useState(dashboardData);

  // Fetch filter data on component mount (only for admins)
  useEffect(() => {
    if (isAdmin()) {
      const fetchFilterData = async () => {
        try {
          const [stagesRes, departmentsRes] = await Promise.all([
            axios.get('/api/project-stages', { withCredentials: true }),
            axios.get('/api/departments', { withCredentials: true })
          ]);
          console.log('🔧 Filter data fetched:', {
            stages: stagesRes.data,
            departments: departmentsRes.data
          });
          console.log('🔧 Detailed stages data:', stagesRes.data.map(stage => ({
            id: stage.id,
            stage_number: stage.stage_number,
            name: stage.name,
            description: stage.description
          })));
          setProjectStages(stagesRes.data);
          setDepartments(departmentsRes.data);
        } catch (err) {
          console.error('Error fetching filter data:', err);
        }
      };
      fetchFilterData();
    }
  }, [isAdmin]);

  // Apply filters to dashboard data
  useEffect(() => {
    let filtered = { ...dashboardData };
    
    // Debug logging
    console.log('Dashboard Filtering - Debug Info:', {
      isAdmin: isAdmin(),
      hasProjects: !!dashboardData.projects,
      projectCount: dashboardData.projects?.length,
      hasUsers: !!dashboardData.users,
      userCount: dashboardData.users?.length,
      hasPlannedHours: !!dashboardData.plannedHoursEntries,
      plannedHoursCount: dashboardData.plannedHoursEntries?.length,
      filters,
      anyFiltersActive: !!(filters.projectName || filters.projectStage || filters.department),
      sampleWPSEntries: dashboardData.plannedHoursEntries?.slice(0, 5).map(entry => ({
        id: entry.id,
        project_id: entry.project_id,
        user_id: entry.user_id,
        project_stage_id: entry.project_stage_id,
        hours: entry.hours
      })) || [],
      uniqueWPSStages: [...new Set(dashboardData.plannedHoursEntries?.map(e => e.project_stage_id).filter(Boolean) || [])],
      sampleProjects: dashboardData.projects?.slice(0, 3).map(p => ({
        id: p.id,
        name: p.name,
        project_stage_id: p.project_stage_id
      })) || []
    });
    
    // Only apply filtering logic if user is admin and has data
    if (isAdmin() && dashboardData.projects && dashboardData.users) {
      // Start with all data
      let filteredProjects = [...dashboardData.projects];
      let filteredUsers = [...dashboardData.users];
      let filteredProjectUsers = [...(dashboardData.projectUsers || [])];
      let filteredPlannedHoursEntries = [...(dashboardData.plannedHoursEntries || [])];

      console.log('Starting filtering with:', {
        projects: filteredProjects.length,
        users: filteredUsers.length,
        plannedHours: filteredPlannedHoursEntries.length,
        activeFilters: {
          projectName: filters.projectName,
          projectStage: filters.projectStage,
          department: filters.department
        },
        projectsWithStages: filteredProjects.map(p => ({
          id: p.id,
          name: p.name,
          stage_id: p.project_stage_id
        }))
      });

      // Apply Project Name filter first
      if (filters.projectName) {
        filteredProjects = filteredProjects.filter(project => 
          project.id === parseInt(filters.projectName)
        );
        console.log('After project name filter:', filteredProjects.length, 'projects');
      }

      // Apply Project Stage filter
      if (filters.projectStage) {
        console.log('🔍 STAGE FILTERING DEBUG:', {
          selectedStage: filters.projectStage,
          selectedStageType: typeof filters.projectStage,
          allProjects: filteredProjects.length,
          allWPSEntries: dashboardData.plannedHoursEntries?.length || 0
        });
        
        // For stage filtering, we need to consider both:
        // 1. Projects that have the stage assigned
        // 2. Projects that have WPS entries with the stage assigned
        
        // First, find projects directly assigned to this stage
        const directStageProjects = filteredProjects.filter(project => {
          const match = project.project_stage_id === parseInt(filters.projectStage);
          if (match) {
            console.log(`  📋 Project ${project.id} (${project.name}) has direct stage assignment: ${project.project_stage_id}`);
          }
          return match;
        });
        
        // Debug: Check what stages exist in WPS entries
        const allWPSStages = [...new Set(
          dashboardData.plannedHoursEntries?.map(entry => entry.project_stage_id).filter(Boolean) || []
        )];
        console.log('  🎯 All stages found in WPS entries:', allWPSStages);
        
        // Second, find projects that have WPS entries for this stage
        const wpsEntriesForStage = dashboardData.plannedHoursEntries?.filter(entry => {
          const match = entry.project_stage_id === parseInt(filters.projectStage);
          if (match) {
            console.log(`  📝 WPS entry: Project ${entry.project_id}, User ${entry.user_id}, Stage ${entry.project_stage_id}, Hours ${entry.hours}`);
          }
          return match;
        }) || [];
        
        const projectsWithStageWPS = [...new Set(
          wpsEntriesForStage.map(entry => entry.project_id)
        )];
        
        console.log('  📊 WPS Analysis:', {
          wpsEntriesForSelectedStage: wpsEntriesForStage.length,
          uniqueProjectsFromWPS: projectsWithStageWPS.length,
          projectIdsFromWPS: projectsWithStageWPS
        });
        
        // Combine both lists and remove duplicates
        const allStageProjectIds = [...new Set([
          ...directStageProjects.map(p => p.id),
          ...projectsWithStageWPS
        ])];
        
        // Filter projects to include all projects related to this stage
        filteredProjects = filteredProjects.filter(project => 
          allStageProjectIds.includes(project.id)
        );
        
        console.log('🎯 STAGE FILTER RESULTS:', {
          selectedStage: filters.projectStage,
          directStageProjects: directStageProjects.length,
          projectsWithStageWPS: projectsWithStageWPS.length,
          combinedProjectIds: allStageProjectIds,
          finalFilteredProjects: filteredProjects.length,
          finalProjectDetails: filteredProjects.map(p => ({
            id: p.id, 
            name: p.name, 
            directStage: p.project_stage_id
          }))
        });
      }

      // Apply Department filter
      if (filters.department) {
        // For department filtering, we need to consider both:
        // 1. Projects that have users from this department (through project assignments)
        // 2. Projects that have WPS entries from users in this department
        
        const departmentUsers = dashboardData.users.filter(user => 
          user.department_id === parseInt(filters.department)
        );
        const departmentUserIds = departmentUsers.map(u => u.id);
        
        console.log('Department filter - users in department:', departmentUsers.length, 
          'User IDs:', departmentUserIds);
        
        // First, find projects that have users from this department (through project assignments)
        const projectsWithDepartmentUsers = [...new Set(
          dashboardData.projectUsers
            ?.filter(pu => departmentUserIds.includes(pu.user_id))
            .map(pu => pu.project_id) || []
        )];
        
        // Second, find projects that have WPS entries from users in this department
        const projectsWithDepartmentWPS = [...new Set(
          dashboardData.plannedHoursEntries
            ?.filter(entry => departmentUserIds.includes(entry.user_id))
            .map(entry => entry.project_id) || []
        )];
        
        // Combine both lists to get all projects related to this department
        const allDepartmentProjectIds = [...new Set([
          ...projectsWithDepartmentUsers,
          ...projectsWithDepartmentWPS
        ])];
        
        console.log('After department filter analysis:', 
          'Looking for department ID:', filters.department,
          'Projects with department users (assignments):', projectsWithDepartmentUsers.length,
          'Projects with department WPS entries:', projectsWithDepartmentWPS.length,
          'Combined department project IDs:', allDepartmentProjectIds);
        
        // If we have other filters, intersect with existing filtered projects
        // If department is the only filter, start with all projects that have users from this department
        if (filters.projectName || filters.projectStage) {
          // Intersect with already filtered projects
          filteredProjects = filteredProjects.filter(project => 
            allDepartmentProjectIds.includes(project.id)
          );
        } else {
          // Department is the only filter, show all projects with users from this department
          filteredProjects = dashboardData.projects.filter(project => 
            allDepartmentProjectIds.includes(project.id)
          );
        }
        
        console.log('After department filter:', filteredProjects.length, 'projects');
      }

      // Now filter related data based on filtered projects
      const filteredProjectIds = filteredProjects.map(p => p.id);

      // Filter projectUsers to only include relationships with filtered projects
      filteredProjectUsers = dashboardData.projectUsers?.filter(pu => 
        filteredProjectIds.includes(pu.project_id)
      ) || [];

      // Filter users based on the filtering strategy
      if (filters.department && !filters.projectName && !filters.projectStage) {
        // Department-only filter: show all users from that department (regardless of project assignment)
        filteredUsers = dashboardData.users.filter(user => 
          user.department_id === parseInt(filters.department)
        );
      } else {
        // Other filters: show users assigned to filtered projects
        const userIdsFromFilteredProjects = filteredProjectUsers.map(pu => pu.user_id);
        filteredUsers = dashboardData.users?.filter(user => 
          userIdsFromFilteredProjects.includes(user.id)
        ) || [];

        // If department filter is also active, additionally filter by department
        if (filters.department) {
          filteredUsers = filteredUsers.filter(user => 
            user.department_id === parseInt(filters.department)
          );
        }
      }

      // Filter plannedHoursEntries based on filtered data
      filteredPlannedHoursEntries = dashboardData.plannedHoursEntries?.filter(entry => {
        const projectMatch = filteredProjectIds.includes(entry.project_id);
        const userMatch = filteredUsers.some(user => user.id === entry.user_id);
        
        // Apply stage filter to WPS entries if stage filter is active
        const stageMatch = filters.projectStage ? 
          entry.project_stage_id === parseInt(filters.projectStage) : true;
        
        // Apply department filter to WPS entries if department filter is active
        let departmentMatch = true;
        if (filters.department) {
          // Check if the entry's user belongs to the selected department
          const entryUser = dashboardData.users.find(u => u.id === entry.user_id);
          departmentMatch = entryUser?.department_id === parseInt(filters.department);
        }
        
        return projectMatch && userMatch && stageMatch && departmentMatch;
      }) || [];

      console.log('Final filtered data:', {
        projects: filteredProjects.length,
        users: filteredUsers.length,
        projectUsers: filteredProjectUsers.length,
        plannedHours: filteredPlannedHoursEntries.length
      });

      // Recalculate dashboard metrics based on filtered data
      const totalPlannedHours = filteredProjects.reduce((sum, p) => sum + (p.planned_hours || 0), 0);
      const totalActualHours = filteredProjects.reduce((sum, p) => sum + (p.actual_hours || 0), 0);
      const utilization = totalPlannedHours > 0 ? (totalActualHours / totalPlannedHours) * 100 : 0;

      filtered = {
        ...dashboardData,
        projects: filteredProjects,
        users: filteredUsers,
        projectUsers: filteredProjectUsers,
        plannedHoursEntries: filteredPlannedHoursEntries,
        totalProjects: filteredProjects.length,
        teamMembers: filteredUsers.length,
        plannedHours: totalPlannedHours,
        actualHours: totalActualHours,
        utilization
      };
    } else {
      console.log('Skipping filtering - reasons:', {
        notAdmin: !isAdmin(),
        noProjects: !dashboardData.projects,
        noUsers: !dashboardData.users
      });
    }

    setFilteredDashboardData(filtered);
  }, [dashboardData, filters, isAdmin]);

  // Handle filter changes
  const handleFilterChange = (filterType, value) => {
    console.log('🔄 Filter change:', {
      filterType,
      value,
      valueType: typeof value,
      previousFilters: filters
    });
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  // Clear all filters
  const clearFilters = () => {
    setFilters({
      projectName: '',
      projectStage: '',
      department: ''
    });
  };

  // Debug logging
  console.log('Dashboard - Raw projects from context:', projects);
  console.log('Dashboard - Dashboard data projects:', dashboardData.projects);
  console.log('Dashboard - Projects count:', dashboardData.projects?.length || 0);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
        <Typography variant="h6" sx={{ ml: 2 }}>Loading dashboard data...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6" color="error" gutterBottom>
          {error}
        </Typography>
        <Typography>
          Please try refreshing the page or contact support if the problem persists.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{
        mb: 4,
        pb: 2,
        borderBottom: '1px solid rgba(0,0,0,0.1)',
        backgroundImage: 'url(/geometric-pattern.svg)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        borderRadius: 1,
        p: 3,
        position: 'relative',
        overflow: 'hidden',
      }}>
        <Box sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(255,255,255,0.85)',
          zIndex: 0
        }} />
        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <Typography variant="h4" gutterBottom sx={{ color: '#2D3A45', fontWeight: 600 }}>
            Time Management Dashboard
          </Typography>
          <Typography variant="subtitle1" sx={{ color: '#5A6A75' }} gutterBottom>
            Overview of projects and quantity surveyor activities
          </Typography>
        </Box>
      </Box>

      {/* Admin Filters */}
      {isAdmin() && (
        <Box sx={{
          mb: 3,
          p: 2,
          bgcolor: 'rgba(255,255,255,0.8)',
          borderRadius: '12px',
          border: '1px solid rgba(0,0,0,0.1)',
          boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.1)'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <FilterListIcon sx={{ color: '#2D3A45', mr: 1 }} />
            <Typography variant="h6" sx={{ color: '#2D3A45', fontWeight: 500 }}>
              Dashboard Filters
            </Typography>
            <Typography variant="caption" sx={{ ml: 1, color: '#5A6A75' }}>
              (Admin View)
            </Typography>
            {(filters.projectName || filters.projectStage || filters.department) && (
              <Button
                onClick={clearFilters}
                size="small"
                sx={{ ml: 'auto', color: '#FF5722', '&:hover': { bgcolor: 'rgba(255, 87, 34, 0.04)' } }}
              >
                Clear All
              </Button>
            )}
          </Box>
          
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 2 }}>
            {/* Project Name Filter */}
            <FormControl size="small">
              <InputLabel sx={{ '&.Mui-focused': { color: '#FF5722' } }}>Project Name</InputLabel>
              <Select
                value={filters.projectName}
                label="Project Name"
                onChange={(e) => handleFilterChange('projectName', e.target.value)}
                sx={{
                  bgcolor: 'white',
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#FF5722'
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#FF5722'
                  }
                }}
              >
                <MenuItem value="">All Projects</MenuItem>
                {dashboardData.allProjects?.map((project) => (
                  <MenuItem key={project.id} value={project.id}>
                    {project.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Project Stage Filter */}
            <FormControl size="small">
              <InputLabel sx={{ '&.Mui-focused': { color: '#FF5722' } }}>Project Stage</InputLabel>
              <Select
                value={filters.projectStage}
                label="Project Stage"
                onChange={(e) => handleFilterChange('projectStage', e.target.value)}
                sx={{
                  bgcolor: 'white',
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#FF5722'
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#FF5722'
                  }
                }}
              >
                <MenuItem value="">All Stages</MenuItem>
                {projectStages.map((stage) => (
                  <MenuItem key={stage.id} value={stage.id}>
                    {stage.stage_number > 6 ? stage.name : `Stage ${stage.stage_number}: ${stage.name}`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Department Filter */}
            <FormControl size="small">
              <InputLabel sx={{ '&.Mui-focused': { color: '#FF5722' } }}>Department</InputLabel>
              <Select
                value={filters.department}
                label="Department"
                onChange={(e) => handleFilterChange('department', e.target.value)}
                sx={{
                  bgcolor: 'white',
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#FF5722'
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#FF5722'
                  }
                }}
              >
                <MenuItem value="">All Departments</MenuItem>
                {departments.map((dept) => (
                  <MenuItem key={dept.id} value={dept.id}>
                    {dept.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* Filter Results Summary */}
          {(filters.projectName || filters.projectStage || filters.department) && (
            <Box sx={{ mt: 2, p: 1, bgcolor: 'rgba(76, 175, 80, 0.1)', borderRadius: 1 }}>
              <Typography variant="body2" sx={{ color: '#2D3A45' }}>
                Showing {filteredDashboardData.projects?.length || 0} of {dashboardData.projects?.length || 0} projects
                {filters.projectName && ` • Project: ${dashboardData.allProjects?.find(p => p.id === parseInt(filters.projectName))?.name}`}
                {filters.projectStage && ` • Stage: ${projectStages.find(s => s.id === parseInt(filters.projectStage))?.name}`}
                {filters.department && ` • Department: ${departments.find(d => d.id === parseInt(filters.department))?.name}`}
              </Typography>
            </Box>
          )}
        </Box>
      )}

      <SummaryCards data={filteredDashboardData} />
      <VarianceCharts data={filteredDashboardData} />

      <Box sx={{ mt: 4 }}>
        <Typography variant="h5" gutterBottom sx={{ color: '#2D3A45', fontWeight: 500, borderLeft: '4px solid #FF5722', pl: 2 }}>
          Projects
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 2 }}>
          {filteredDashboardData.projects.map((project, index) => (
            <Paper
              key={project.id || index}
              sx={{
                p: 2,
                cursor: 'pointer',
                transition: 'transform 0.2s, box-shadow 0.2s',
                borderRadius: '16px',
                boxShadow: '0px 6px 12px rgba(0, 0, 0, 0.15)',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0px 8px 16px rgba(0, 0, 0, 0.2)',
                }
              }}
              onClick={() => window.location.href = `/projects?id=${project.id}`}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>{project.name}</Typography>
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                  {project.project_stage_id && (
                    <Chip
                      label={`Stage ${project.project_stage_id}`}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  )}
                  {project.job_number && (
                    <Chip
                      label={project.job_number}
                      size="small"
                      color="secondary"
                      variant="outlined"
                    />
                  )}
                </Box>
              </Box>

              <Typography variant="body2" color="text.secondary" gutterBottom>
                {project.description}
              </Typography>

              <Box sx={{ mt: 2, mb: 2 }}>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.5, mb: 1 }}>
                  <Typography variant="body2">
                    <strong>Planned:</strong> {project.planned_hours || 0}h
                  </Typography>
                  <Typography variant="body2">
                    <strong>Logged:</strong> {project.actual_hours || 0}h
                  </Typography>
                  <Typography variant="body2">
                    <strong>Team:</strong> {project.team_size || 0} members
                  </Typography>
                  <Typography variant="body2">
                    <strong>Allowable:</strong> {project.allowable_hours || 'Not Set'}h
                  </Typography>
                </Box>

                {/* Progress Bar */}
                <Box sx={{ mt: 1.5 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="caption" color="text.secondary">Progress</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {project.planned_hours > 0
                        ? `${Math.round((project.actual_hours / project.planned_hours) * 100)}%`
                        : '0%'
                      }
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={project.planned_hours > 0 ? Math.min((project.actual_hours / project.planned_hours) * 100, 100) : 0}
                    sx={{
                      height: 6,
                      borderRadius: 3,
                      backgroundColor: 'grey.200',
                      '& .MuiLinearProgress-bar': {
                        backgroundColor: project.actual_hours > project.planned_hours ? '#f44336' : '#4caf50',
                        borderRadius: 3
                      }
                    }}
                  />
                </Box>


              </Box>
              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  size="small"
                  component={Link}
                  to={`/projects?id=${project.id}`}
                  onClick={(e) => e.stopPropagation()}
                  sx={{
                    color: '#FF5722',
                    borderColor: '#FF5722',
                    '&:hover': {
                      borderColor: '#FF7A50',
                      color: '#FF7A50',
                      bgcolor: 'rgba(255, 87, 34, 0.04)'
                    }
                  }}
                >
                  View Details
                </Button>
              </Box>
            </Paper>
          ))}
          {filteredDashboardData.projects.length === 0 && (
            <Paper sx={{
              p: 3,
              gridColumn: '1 / -1',
              textAlign: 'center',
              borderRadius: '16px',
              boxShadow: '0px 6px 12px rgba(0, 0, 0, 0.15)'
            }}>
              <Typography variant="body1">
                {(filters.projectName || filters.projectStage || filters.department) 
                  ? 'No projects match the current filters. Try adjusting your search criteria.'
                  : 'No projects found. Add your first construction project to get started.'
                }
              </Typography>
              {!(filters.projectName || filters.projectStage || filters.department) && (
                <Button
                  variant="contained"
                  component={Link}
                  to="/projects"
                  sx={{
                    mt: 2,
                    bgcolor: '#2D3A45',
                    '&:hover': { bgcolor: '#3D4A55' }
                  }}
                >
                  Add Project
                </Button>
              )}
            </Paper>
          )}
        </Box>
      </Box>
    </Box>
  );
};

// Projects Page
const Projects = () => (
  <ProjectList />
);

// Users Page
const Users = () => (
  <UserList />
);

// Reports Page
const Reports = () => <ReportsPage />;

// Notifications Page
const Notifications = () => <NotificationsPage />;

// Reconciliation Page
const Reconciliation = () => <PlanningVsActual />;

// Admin Page
const Admin = () => <AdminPage />;

// AI Chat Page
const AIChatPage = () => <AIChat />;

const drawerWidth = 240;

const Main = styled('main', { shouldForwardProp: (prop) => prop !== 'open' })(
  ({ theme, open }) => ({
    flexGrow: 1,
    padding: theme.spacing(3),
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    marginLeft: `-${drawerWidth}px`,
    ...(open && {
      transition: theme.transitions.create('margin', {
        easing: theme.transitions.easing.easeOut,
        duration: theme.transitions.duration.enteringScreen,
      }),
      marginLeft: 0,
    }),
  }),
);

// Main App Component with Authentication
function AuthenticatedApp() {
  // setOpen is not used yet, but will be used for toggling the drawer in desktop view
  // eslint-disable-next-line no-unused-vars
  const [open, setOpen] = React.useState(true);
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const { user, logout, isAdmin } = useAuth();
  const { unreadCount } = useNotifications();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  // Role-based navigation menu
  const menuItems = isAdmin() ? [
    // Admin sees all pages
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/' },
    { text: 'Work Planning', icon: <CalendarTodayIcon />, path: '/planning' },
    { text: 'Time Entry', icon: <AccessTimeIcon />, path: '/time-logging' },
    { text: 'AI Assistant', icon: <SmartToyIcon />, path: '/ai-chat' },
    { text: 'Reconciliation', icon: <CompareArrowsIcon />, path: '/reconciliation' },
    { text: 'Projects', icon: <FolderIcon />, path: '/projects' },
    { text: 'Users', icon: <PeopleIcon />, path: '/users' },
    { text: 'Reports', icon: <AssessmentIcon />, path: '/reports' },
    { text: 'Notifications', icon: <NotificationsIcon />, path: '/notifications' },
    { text: 'Admin', icon: <AdminPanelSettingsIcon />, path: '/admin' }
  ] : [
    // Regular users see only these pages (Reconciliation, Projects, Users, and Reports are admin-only)
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/' },
    { text: 'Work Planning', icon: <CalendarTodayIcon />, path: '/planning' },
    { text: 'Time Entry', icon: <AccessTimeIcon />, path: '/time-logging' },
    { text: 'Notifications', icon: <NotificationsIcon />, path: '/notifications' }
  ];

  const drawer = (
    <div>
      <Toolbar sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start', // Changed from center to flex-start to align left
        pt: 1, // Reduced top padding
        pb: 2,
        minHeight: 100,
        pl: 2 // Left padding
      }}>
        <Box sx={{ mt: 2 }}> {/* Increased margin-top to move down */}
          <img
            src="/LDM-logo-light.png"
            alt="LDM Logo"
            style={{
              width: 150,
              height: 'auto',
              marginBottom: 8,
              display: 'block'
            }}
          onError={(e) => {
            console.error("Image failed to load:", e);
            e.target.onerror = null;
            e.target.src = "/ldm-logo.svg"; // Fallback to SVG if PNG fails
          }}
        />
        </Box>
        {/* Space kept but text removed */}
        <Box sx={{ height: 20, width: '100%' }}></Box>
      </Toolbar>
      <List>
        {menuItems.map((item) => (
          <ListItem
            key={item.text}
            component={Link}
            to={item.path}
            sx={{
              py: 1.5, // Add more vertical padding
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
              },
              '&.Mui-selected': {
                backgroundColor: 'rgba(255, 87, 34, 0.2)',
                '&:hover': {
                  backgroundColor: 'rgba(255, 87, 34, 0.3)',
                },
              },
            }}
          >
            <ListItemIcon sx={{ color: '#FF5722', minWidth: 40, '& svg': { fontSize: '1.5rem' } }}>
              {item.text === 'Notifications' && unreadCount > 0 ? (
                <Badge badgeContent={unreadCount} color="error">
                  {item.icon}
                </Badge>
              ) : (
                item.icon
              )}
            </ListItemIcon>
            <ListItemText
              primary={item.text}
              primaryTypographyProps={{
                variant: 'h6',
                fontSize: '1.25rem',
                fontWeight: 500,
                fontFamily: 'Roboto, "Helvetica Neue", Arial, sans-serif'
              }}
            />
          </ListItem>
        ))}
      </List>
    </div>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar
          position="fixed"
          sx={{
            width: { sm: `calc(100% - ${drawerWidth}px)` },
            ml: { sm: `${drawerWidth}px` },
            boxShadow: 1,
            backgroundImage: 'linear-gradient(to right, #2D3A45, #3D4A55)',
          }}
        >
          <Toolbar sx={{ minHeight: 100, py: 2 }}>
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2, display: { sm: 'none' } }}
            >
              <MenuIcon />
            </IconButton>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="h4" noWrap component="div" sx={{
                  fontSize: '2.5rem',
                  fontWeight: 600,
                  fontFamily: 'Roboto, "Helvetica Neue", Arial, sans-serif'
                }}>
                  Time Management
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="body1" sx={{ color: 'white' }}>
                  Welcome, {user?.name}
                </Typography>
                <NotificationCenter />
                <Button
                  color="inherit"
                  onClick={logout}
                  startIcon={<LogoutIcon />}
                  sx={{
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    }
                  }}
                >
                  Logout
                </Button>
              </Box>
            </Box>
          </Toolbar>
        </AppBar>
        <Box
          component="nav"
          sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
          aria-label="mailbox folders"
        >
          <Drawer
            variant="temporary"
            open={mobileOpen}
            onClose={handleDrawerToggle}
            ModalProps={{
              keepMounted: true, // Better open performance on mobile.
            }}
            sx={{
              display: { xs: 'block', sm: 'none' },
              '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
            }}
          >
            {drawer}
          </Drawer>
          <Drawer
            variant="permanent"
            sx={{
              display: { xs: 'none', sm: 'block' },
              '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
            }}
            open
          >
            {drawer}
          </Drawer>
        </Box>
        <Main open={open}>
          <Toolbar sx={{ minHeight: 100 }} />
          <Container maxWidth="xl">
            <Routes>
              <Route path="/" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
              <Route path="/planning" element={<ProtectedRoute><WeeklyPlan /></ProtectedRoute>} />
              <Route path="/time-logging" element={<ProtectedRoute><DailyEntry /></ProtectedRoute>} />
              <Route path="/ai-chat" element={<ProtectedRoute requireAdmin={true}><AIChatPage /></ProtectedRoute>} />
              <Route path="/reconciliation" element={<ProtectedRoute requireAdmin={true}><Reconciliation /></ProtectedRoute>} />
              <Route path="/projects" element={<ProtectedRoute requireAdmin={true}><Projects /></ProtectedRoute>} />
              <Route path="/users" element={<ProtectedRoute requireAdmin={true}><Users /></ProtectedRoute>} />
              <Route path="/reports" element={<ProtectedRoute requireAdmin={true}><Reports /></ProtectedRoute>} />
              <Route path="/notifications" element={<ProtectedRoute><Notifications /></ProtectedRoute>} />
              <Route path="/admin" element={<ProtectedRoute requireAdmin={true}><Admin /></ProtectedRoute>} />
            </Routes>
          </Container>
        </Main>
      </Box>
  );
}

// Loading component
const LoadingScreen = () => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      gap: 2
    }}
  >
    <CircularProgress size={60} />
    <Typography variant="h6" color="text.secondary">
      Loading Time Management System...
    </Typography>
  </Box>
);

// Main App Component
function App() {
  const { isLoading, error, logout } = useAuth0();
  const { isAuthenticated, loading: authLoading } = useAuth();

  // Combine Auth0 loading with our custom auth loading
  const totalLoading = isLoading || authLoading;

  // Handle Auth0 errors by clearing cache and showing login
  // MUST be at top level, not inside conditional
  React.useEffect(() => {
    if (error) {
      console.log('❌ Auth0 Error detected:', error);

      // Clear Auth0 cache without calling logout
      try {
        Object.keys(localStorage).forEach(key => {
          if (key.includes('@@auth0spajs@@')) {
            localStorage.removeItem(key);
          }
        });

        // Also clear session storage
        Object.keys(sessionStorage).forEach(key => {
          if (key.includes('@@auth0spajs@@')) {
            sessionStorage.removeItem(key);
          }
        });

        console.log('✅ Auth0 cache cleared');
      } catch (e) {
        console.warn('Could not clear Auth0 cache:', e);
      }

      // Redirect to login without calling logout
      console.log('🔄 Redirecting to login page');
      window.location.href = '/login';
    }
  }, [error]);

  // Debug routing logic
  console.log('🔄 App render:', {
    isLoading,
    authLoading,
    totalLoading,
    isAuthenticatedResult: isAuthenticated(),
    error: !!error
  });

  // Enhanced error handling with Auth0 session clearing
  if (error) {
    console.log('❌ Auth0 Error detected:', error);

    // Clear Auth0 cache and redirect to login
    const handleClearAndRetry = () => {
      try {
        // Clear all storage
        Object.keys(localStorage).forEach(key => {
          if (key.includes('@@auth0spajs@@')) {
            localStorage.removeItem(key);
          }
        });

        Object.keys(sessionStorage).forEach(key => {
          if (key.includes('@@auth0spajs@@')) {
            sessionStorage.removeItem(key);
          }
        });

        console.log('✅ Cache cleared, redirecting to login');

        // Redirect to login without calling logout
        window.location.href = '/login';
      } catch (e) {
        console.error('Error clearing cache:', e);
        // Fallback: just redirect to login
        window.location.href = '/login';
      }
    };

    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          gap: 2,
          p: 3
        }}
      >
        <Typography variant="h4" color="error" gutterBottom>
          Authentication Error
        </Typography>
        <Typography variant="body1" color="text.secondary" textAlign="center">
          {error.message || 'An authentication error occurred. Please sign in again.'}
        </Typography>
        <Button
          variant="contained"
          onClick={handleClearAndRetry}
          sx={{ mt: 2 }}
        >
          Sign In Again
        </Button>
      </Box>
    );
  }

  if (totalLoading) {
    console.log('⏳ Showing loading screen');
    return <LoadingScreen />;
  }

  const userIsAuthenticated = isAuthenticated();
  console.log('🔀 Router decision:', {
    userIsAuthenticated,
    willShow: userIsAuthenticated ? 'AuthenticatedApp' : 'Auth0Login'
  });

  return (
    <Router>
      <Routes>
        <Route
          path="/login"
          element={!userIsAuthenticated ? <Auth0Login /> : (
            <NotificationProvider>
              <AuthenticatedApp />
            </NotificationProvider>
          )}
        />
        <Route
          path="/*"
          element={userIsAuthenticated ? (
            <NotificationProvider>
              <AuthenticatedApp />
            </NotificationProvider>
          ) : <Auth0Login />}
        />
      </Routes>
    </Router>
  );
}

export default App;
